import { Item } from './Item';
import { ItemType } from './ItemType';
import { MenuItem } from './MenuItem';
import { NonSequencedFlowItem } from './NonSequencedFlowItem';
import { Quiz } from './Quiz';
import { ResourcesFolder } from './ResourcesFolder';
import { SequencedFlowItem } from './SequencedFlowItem';
import { Guid } from '../types/identifiers/guid';

/**
 * The item types that can be created through the `EditAction_CreateNewItem` and
 * `EditAction_CreateResourcesFolder` actions - i.e. the items that live in the menu tree. Content
 * items go through `EditAction_CreateNewContentItem` instead (see `NewContentItem`).
 */
export type NewItem = MenuItem | NonSequencedFlowItem | Quiz | ResourcesFolder | SequencedFlowItem;

/** The `ItemType`s of {@link NewItem}, in the same order. */
export const NEW_ITEM_TYPES: readonly ItemType[] = [
  ItemType.MenuItem,
  ItemType.NonSequencedFlow,
  ItemType.Quiz,
  ItemType.ResourcesFolder,
  ItemType.SequencedFlow,
];

export const isNewItem = (item: Item): item is NewItem => NEW_ITEM_TYPES.includes(item.type);

export function ensureNewItem(item: Item): asserts item is NewItem {
  if (!isNewItem(item)) {
    throw Error(
      `Expected a creatable menu tree item, got '${item.type}'. Use EditAction_CreateNewContentItem for content items.`
    );
  }
}

/**
 * Payload of the `EditAction_CreateNewItem` and `EditAction_CreateResourcesFolder` redux actions,
 * dispatched by the manager's `CreateNewItem` / `CreateResourcesFolder` thunks and consumed by the
 * items/root items/edit/selected menu item reducers and the api middleware (`newItemApi`).
 *
 * Build it with {@link createNewItemPayload} rather than by hand.
 */
export type CreateNewItemPayload = {
  /** The already built item - reducers insert it under `parentItemId` as is. */
  readonly item: NewItem;
  /** Id of the menu item the new item is added under, `null` when it goes to the root. Always `null` for a resources folder. */
  readonly parentItemId: Guid | null;
};

export type CreateNewItemPayloadOptions = {
  /** Defaults to the parent the item was built with (`item.parentId.parentId`). `null` puts the item in the root. */
  parentItemId?: Guid | null;
};

/**
 * Builds the payload for the `EditAction_CreateNewItem` / `EditAction_CreateResourcesFolder` action.
 *
 * The item type is checked both at compile time ({@link NewItem}) and at runtime, and the parent is
 * resolved from the item itself when not given. An empty parent id is normalised to `null` (root).
 */
export const createNewItemPayload = (
  item: NewItem,
  { parentItemId }: CreateNewItemPayloadOptions = {}
): CreateNewItemPayload => {
  ensureNewItem(item);

  const parent = (parentItemId === undefined ? item.parentId.parentId : parentItemId) || null;
  if (item.type === ItemType.ResourcesFolder && parent !== null) {
    throw Error(`Resources folder '${item.id}' cannot have a parent - it always sits in the root.`);
  }

  return { item, parentItemId: parent };
};
